import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell, { storedSession } from '../components/AppShell';
import { notificationTarget } from '../components/NotificationBell';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, Pagination, SkeletonRows, Tabs, timeAgo, ToastProvider, useToast } from '../components/ui/Kit';

function Preferences() {
  const toast = useToast();
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/notifications/preferences').then(setP).catch((e) => setError(e.message)); }, []);
  const save = async () => {
    try { setP(await api('/api/notifications/preferences', { method: 'PUT', body: { categories: p.categories, daily_digest: p.daily_digest } })); toast.show('Preferences saved'); }
    catch (e) { setError(e.message); }
  };
  if (!p) return <div className={card + ' p-5'}>{error ? <InlineError>{error}</InlineError> : <p className="text-sm text-[#64748B]">Loading…</p>}</div>;
  const setCat = (key, field, val) => setP({ ...p, categories: p.categories.map((c) => (c.key === key ? { ...c, [field]: val } : c)) });
  return (
    <section className={card + ' p-5'} aria-labelledby="prefs-title">
      <h2 id="prefs-title" className="text-base font-extrabold">Preferences</h2>
      <p className="text-sm text-[#64748B] mt-0.5 mb-4">Choose what reaches you in Quali5d and by e-mail.</p>
      <InlineError>{error}</InlineError>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-[#64748B]"><th className="text-left font-semibold pb-2">Type</th><th className="font-semibold pb-2 w-24">In Quali5d</th><th className="font-semibold pb-2 w-24">E-mail</th></tr></thead>
        <tbody>
          {p.categories.map((c) => (
            <tr key={c.key} className="border-t border-[#E6E9F0]">
              <td className="py-3"><p className="font-semibold">{c.label}</p><p className="text-xs text-[#64748B]">{c.hint}</p></td>
              <td className="text-center"><input type="checkbox" aria-label={`${c.label} in Quali5d`} checked={c.in_app} onChange={(e) => setCat(c.key, 'in_app', e.target.checked)} /></td>
              <td className="text-center"><input type="checkbox" aria-label={`${c.label} by e-mail`} checked={c.email} onChange={(e) => setCat(c.key, 'email', e.target.checked)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="flex items-start gap-2.5 text-sm mt-4">
        <input type="checkbox" className="mt-0.5" checked={p.daily_digest} onChange={(e) => setP({ ...p, daily_digest: e.target.checked })} />
        <span><span className="font-semibold">Daily digest</span><span className="block text-xs text-[#64748B]">One e-mail a day at {String(p.digest_hour).padStart(2, '0')}:00 ({p.timezone}) instead of one per notification.</span></span>
      </label>
      <div className="flex justify-end mt-4"><button className={btnPrimary} onClick={save}>Save preferences</button></div>
    </section>
  );
}

function Inner() {
  const navigate = useNavigate();
  const { user } = storedSession();
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setData(await api(`/api/notifications?filter=${tab}&page=${page}&page_size=20`)); } catch (e) { setError(e.message); setData({ items: [], total: 0, unread: 0 }); }
  }, [tab, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab]);
  const act = async (path) => { try { await api(path, { method: 'POST' }); load(); } catch (e) { setError(e.message); } };
  const open = async (n) => {
    try { if (!n.is_read) await api(`/api/notifications/${n.id}/mark-read`, { method: 'POST' }); } catch (e) { /* ignore */ }
    navigate(notificationTarget(n, user.role));
  };
  return (
    <AppShell active="">
      <div className="flex items-end justify-between mb-5">
        <div><h1 className="text-2xl font-extrabold">Notifications</h1><p className="text-sm text-[#64748B] mt-1">Approvals, assignments and job updates for you.</p></div>
        <div className="flex gap-2">
          <button className={btnSecondary} onClick={() => act('/api/notifications/read-all')}>Mark all read</button>
          <button className={btnSecondary} onClick={() => act('/api/notifications/clear-all')}>Clear all</button>
        </div>
      </div>
      <div className="grid xl:grid-cols-[1fr_380px] gap-5 items-start">
        <section className={card}>
          <div className="px-4 pt-3"><Tabs tabs={[{ key: 'all', label: 'All' }, { key: 'unread', label: `Unread${data && data.unread ? ` (${data.unread})` : ''}` }]} active={tab} onChange={setTab} label="Show" /></div>
          <InlineError>{error}</InlineError>
          {data === null ? <table className="w-full"><SkeletonRows cols={2} /></table> : !data.items.length ? (
            <EmptyState title={tab === 'unread' ? 'Nothing unread' : "You're all caught up"} hint="New approvals and assignments appear here." />
          ) : (
            <ul>
              {data.items.map((n) => (
                <li key={n.id}>
                  <button onClick={() => open(n)} className={`w-full text-left px-5 py-4 border-t border-[#E6E9F0] hover:bg-[#F5F7FB] flex gap-3 ${n.is_read ? '' : 'bg-[#F7FAFE]'}`}>
                    <span aria-hidden="true" className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.is_read ? '' : 'bg-[#0066CC]'}`} />
                    <span className="min-w-0"><span className="block text-sm font-semibold">{n.title}</span>
                      {n.message && <span className="block text-sm text-[#64748B]">{n.message}</span>}
                      <span className="block text-xs text-[#94A3B8] mt-0.5">{timeAgo(n.created_at)}</span></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {data && data.total > 20 && <Pagination page={page} pageSize={20} total={data.total} onPage={setPage} noun="notifications" />}
        </section>
        <Preferences />
      </div>
    </AppShell>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  useEffect(() => { if (!localStorage.getItem('token')) navigate('/'); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <ToastProvider><Inner /></ToastProvider>;
}
